import { memo, useCallback, useMemo, useRef, useState } from 'react';

import { SortDirection } from '@air/api/types';
import { ArrowDownIcon, ArrowUpDownIcon, CheckIcon, ChevronDownIcon } from '@air/design-icons';
import { Button } from '@air/primitive-button';
import { ButtonGroup } from '@air/primitive-button-group';
import { type DropdownMenuOption } from '@air/primitive-dropdown-menu';
import { IconButton } from '@air/primitive-icon-button';
import { Tooltip } from '@air/primitive-tooltip';
import { cn } from '@air/utils-tailwind';

import { DropdownMenuWithActionSheet } from '~/components/Menus/DropdownMenuWithActionSheet';
import { SortMenuDirectionControl } from '~/components/SortButtons/SortMenuDirectionControl';
import { getConfigFieldLabel } from '~/constants/SortFieldsMap';
import { SORT_MENU_TRIGGER_CARET } from '~/constants/testIDs';
import { useTemporaryFeatureFlags } from '~/hooks/useTemporaryFeatureFlags';

export type SortMenuProps = {
  direction: SortDirection;
  currentSortBy: string;
  onSortChange: (sortBy: string, direction: SortDirection) => void;
  sortOptions: readonly string[];
  showDirectionInMenu?: boolean;
  /**
   * Collapses the trigger to an icon-only button (e.g. when the right panel is open). Pair with `showDirectionInMenu`
   * so the direction stays reachable. Only honored behind `ui2026`: the legacy trigger collapses on
   * `showDirectionInMenu` alone.
   */
  compact?: boolean;
  supportsDirection?: (fieldName: string) => boolean;
  'data-testid'?: string;
};

const defaultSupportsDirection = (fieldName: string) => fieldName !== 'customSort';

export const SortMenu = memo(
  ({
    onSortChange,
    currentSortBy,
    direction,
    sortOptions,
    showDirectionInMenu,
    compact,
    supportsDirection = defaultSupportsDirection,
    'data-testid': testId,
  }: SortMenuProps) => {
    // Read here rather than at the call sites: canvases and showcases still use the legacy trigger until they
    // opt into `showDirectionInMenu`. Public boards do that when `ui2026` is on.
    const { ui2026 } = useTemporaryFeatureFlags();
    const currentFieldSupportsDirection = supportsDirection(currentSortBy);
    const showIconTrigger = ui2026 ? !!compact : !!showDirectionInMenu;

    const onSortDirectionChange = useCallback(
      (newDirection: SortDirection) => onSortChange(currentSortBy, newDirection),
      [currentSortBy, onSortChange],
    );

    const handleDirectionButtonClick = useCallback(() => {
      onSortDirectionChange(direction === SortDirection.desc ? SortDirection.asc : SortDirection.desc);
    }, [direction, onSortDirectionChange]);

    const getSelectedSuffix = useCallback(() => {
      if (showDirectionInMenu && currentFieldSupportsDirection) {
        return (
          <ArrowDownIcon
            className={cn(
              'ads:text-icon-primary text-altitude-10',
              direction === SortDirection.asc ? 'rotate-180' : 'rotate-0',
            )}
            size="sm"
          />
        );
      }

      return <CheckIcon size="sm" />;
    }, [currentFieldSupportsDirection, direction, showDirectionInMenu]);

    const options = useMemo(() => {
      const optionsArray: DropdownMenuOption[] = [
        {
          id: 'sort-by',
          label: 'Sort by',
          type: 'title',
        },
      ];

      if (ui2026 && showDirectionInMenu && currentFieldSupportsDirection) {
        optionsArray.push(
          {
            children: <SortMenuDirectionControl direction={direction} onChange={onSortDirectionChange} />,
            id: 'direction',
            type: 'children',
          },
          {
            id: 'direction-separator',
            type: 'separator',
          },
        );
      }

      sortOptions.map((option) => {
        if (option === 'customSort') {
          optionsArray.push({
            id: 'custom-sort-separator',
            type: 'separator',
          });
        }

        const isSelected = option === currentSortBy;

        if (ui2026) {
          // The redesign moves direction into its own control above the list, so items only ever pick the field.
          return optionsArray.push({
            id: option,
            label: getConfigFieldLabel(option),
            onSelect: () => onSortChange(option, direction),
            suffix: isSelected ? <CheckIcon size="sm" /> : null,
            type: 'item',
          });
        }

        return optionsArray.push({
          id: option,
          label: getConfigFieldLabel(option),
          onSelect: () => {
            if (showDirectionInMenu && isSelected && supportsDirection(option)) {
              onSortChange(option, direction === SortDirection.desc ? SortDirection.asc : SortDirection.desc);
            } else {
              onSortChange(option, direction);
            }
          },
          suffix: isSelected ? getSelectedSuffix() : null,
          type: 'item',
        });
      });

      return optionsArray;
    }, [
      currentFieldSupportsDirection,
      currentSortBy,
      direction,
      getSelectedSuffix,
      onSortChange,
      onSortDirectionChange,
      showDirectionInMenu,
      sortOptions,
      supportsDirection,
      ui2026,
    ]);

    const tooltipLabel = useMemo(() => {
      const fieldLabel = getConfigFieldLabel(currentSortBy);

      if (!currentFieldSupportsDirection) {
        return fieldLabel;
      }

      return `${fieldLabel} ${direction === SortDirection.asc ? '↑' : '↓'}`;
    }, [currentFieldSupportsDirection, currentSortBy, direction]);

    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isTooltipOpen, setIsTooltipOpen] = useState(false);
    const swallowNextTooltipOpenRef = useRef<boolean>(false);

    const handleMenuOpenChange = useCallback((open: boolean) => {
      setIsMenuOpen(open);

      // Closing the menu restores focus to the trigger, and Radix opens a tooltip on focus — leaving
      // it hanging over the toolbar until the pointer happens to move. Drop that one open request;
      // the next genuine hover gets its tooltip as usual.
      if (!open) {
        swallowNextTooltipOpenRef.current = true;
      }
    }, []);

    const handleTooltipOpenChange = useCallback((open: boolean) => {
      if (open && swallowNextTooltipOpenRef.current) {
        swallowNextTooltipOpenRef.current = false;
        return;
      }

      setIsTooltipOpen(open);
    }, []);

    const labeledTriggerSuffix = useMemo(() => {
      if (!ui2026) {
        return undefined;
      }

      if (!currentFieldSupportsDirection || !showDirectionInMenu) {
        return <ChevronDownIcon size="xs" />;
      }

      return (
        <ChevronDownIcon
          className={cn('transition-transform', direction === SortDirection.asc ? 'rotate-180' : 'rotate-0')}
          data-direction={direction}
          data-testid={SORT_MENU_TRIGGER_CARET}
          size="xs"
        />
      );
    }, [currentFieldSupportsDirection, direction, showDirectionInMenu, ui2026]);

    const trigger = showIconTrigger ? (
      <IconButton
        adsIcon
        appearance="ghost"
        color="gray"
        // biome-ignore lint/plugin: PLA-1724 — pre-existing debt, not introduced by the icon migration
        data-testid={`${testId}-MENU`}
        label={tooltipLabel}
        size="small"
        icon={ArrowUpDownIcon}
      />
    ) : (
      <Button
        appearance="ghost"
        color="gray"
        data-direction={ui2026 && currentFieldSupportsDirection && showDirectionInMenu ? direction : undefined}
        // biome-ignore lint/plugin: PLA-1724 — pre-existing debt, not introduced by the icon migration
        data-testid={`${testId}-MENU`}
        size="small"
        suffix={labeledTriggerSuffix}
      >
        {getConfigFieldLabel(currentSortBy)}
      </Button>
    );

    return (
      <ButtonGroup>
        {showDirectionInMenu ? (
          <Tooltip label={tooltipLabel} onOpenChange={handleTooltipOpenChange} open={isTooltipOpen && !isMenuOpen}>
            <div>
              <DropdownMenuWithActionSheet
                align="end"
                onChange={handleMenuOpenChange}
                options={options}
                trigger={trigger}
                title="Sort by"
              />
            </div>
          </Tooltip>
        ) : (
          <DropdownMenuWithActionSheet align="end" options={options} trigger={trigger} title="Sort by" />
        )}
        {currentFieldSupportsDirection && !showDirectionInMenu && (
          <Tooltip label="Sort ascending/descending">
            <IconButton
              appearance="ghost"
              color="gray"
              // biome-ignore lint/plugin: PLA-1724 — pre-existing debt, not introduced by the icon migration
              data-testid={`${testId}-DIRECTION`}
              data-sort={currentSortBy}
              data-direction={direction}
              label="Sort ascending/descending"
              onClick={handleDirectionButtonClick}
              size="small"
            >
              <ArrowDownIcon
                className={cn('transition-transform', direction === SortDirection.asc ? 'rotate-180' : 'rotate-0')}
                data-direction={direction}
                size="xs"
              />
            </IconButton>
          </Tooltip>
        )}
      </ButtonGroup>
    );
  },
);

SortMenu.displayName = 'SortMenu';
